import { useMemo } from 'react';

import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { AlertTriangleIcon, GripVerticalIcon, Trash2Icon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { CatalogBuckets, findCatalogEntry } from '@/modules/aiAgents/hooks/useModelStrategyCatalog';
import { ModelStrategyStep, ReasoningEffort } from '@/shared/types/modelStrategy';

import { ModelPicker } from './ModelPicker';
import { ReasoningEffortSelect } from './ReasoningEffortSelect';

type ChainStepRowProps = {
  /** Stable sortable id owned by ChainEditor. */
  id: string;
  position: number;
  step: ModelStrategyStep;
  buckets: CatalogBuckets;
  disabled?: boolean;
  showRemove?: boolean;
  showReorder?: boolean;
  voiceWarning?: boolean;
  /** Selector keys used by other steps in the chain — disabled in this row's picker. */
  usedKeys?: ReadonlySet<string>;
  onChange: (next: ModelStrategyStep) => void;
  onRemove: () => void;
};

export const ChainStepRow = ({
  id,
  position,
  step,
  buckets,
  disabled,
  showRemove = true,
  showReorder = true,
  voiceWarning,
  usedKeys,
  onChange,
  onRemove,
}: ChainStepRowProps) => {
  const { t } = useTranslation();

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
    disabled: !showReorder || disabled,
  });

  const style = { transform: CSS.Translate.toString(transform), transition };

  // BYOLLM rows whose referenced language model is no longer in the brain's
  // catalog surface a warning chip so the user knows the orchestrator will
  // silently skip this step at runtime.
  const isUnavailableByollm = useMemo(
    () =>
      !!step.language_model_id &&
      !buckets.external.some((entry) => entry.language_model_id === step.language_model_id),
    [step.language_model_id, buckets.external]
  );

  // Reasoning effort only applies to models that support it.
  const supportsReasoning = useMemo(
    () => findCatalogEntry(buckets, step)?.supports_reasoning ?? false,
    [buckets, step]
  );

  const handleReasoningChange = (next: ReasoningEffort) => {
    onChange({ ...step, reasoning_effort: next });
  };

  // When the picked model changes, clear a stale reasoning value if the new
  // model can't reason.
  const handleModelChange = (next: Partial<ModelStrategyStep>) => {
    const merged = { ...step, ...next };
    const supports = findCatalogEntry(buckets, merged)?.supports_reasoning ?? false;
    onChange({ ...merged, reasoning_effort: supports ? merged.reasoning_effort : null });
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn('flex flex-col gap-1', isDragging && 'opacity-50')}
    >
      <div className="flex items-start gap-2">
        <div className="flex min-w-0 flex-1 flex-col gap-2 sm:flex-row">
          <div className="min-w-0 flex-1">
            <ModelPicker
              value={step}
              buckets={buckets}
              disabled={disabled}
              usedKeys={usedKeys}
              placeholder={t('common.select_dots')}
              onChange={handleModelChange}
            />
          </div>
          <div className="min-w-0 flex-1">
            <ReasoningEffortSelect
              value={step.reasoning_effort}
              onChange={handleReasoningChange}
              disabled={disabled}
              unsupported={!supportsReasoning}
            />
          </div>
        </div>

        {(showRemove || showReorder) && (
          <div className="flex h-10 w-16 shrink-0 items-center justify-end gap-2">
            {showRemove && (
              <Button
                type="button"
                variant="outline-neutral"
                size="icon-lg"
                disabled={disabled}
                onClick={onRemove}
                aria-label={t('common.remove')}
                className="text-muted-foreground hover:text-destructive hover:[&_svg]:text-destructive"
              >
                <Trash2Icon className="h-4 w-4" />
              </Button>
            )}
            {showReorder && (
              <span
                role="button"
                aria-label={t('ai_agents.model_strategy.a11y.drag_handle', { 0: position + 1 })}
                aria-roledescription={t('ai_agents.model_strategy.a11y.sortable')}
                aria-disabled={disabled}
                className={cn(
                  'shrink-0 rounded-sm text-muted-foreground ring-offset-background focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:outline-none',
                  disabled ? 'cursor-not-allowed opacity-50' : 'cursor-grab active:cursor-grabbing'
                )}
                {...(disabled ? {} : attributes)}
                {...(disabled ? {} : listeners)}
              >
                <GripVerticalIcon aria-hidden="true" className="h-4 w-4" />
              </span>
            )}
          </div>
        )}
      </div>

      {isUnavailableByollm && (
        <p role="alert" className="flex items-center gap-1 text-xs leading-4 text-warning">
          <AlertTriangleIcon className="h-3 w-3 shrink-0" aria-hidden="true" />
          {t('ai_agents.model_strategy.row_unavailable')}
        </p>
      )}

      {voiceWarning && (
        <p className="text-xs leading-4 text-warning">
          {t('ai_agents.model_strategy.voice_high_reasoning_warning')}
        </p>
      )}
    </div>
  );
};
